import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { LINEUP_PITCH_FRAME_WIDTH_OVER_HEIGHT } from './lineupPitchLayout.constants';

export function getMatchHubLineupsStyles(theme: Theme, isDark: boolean) {
  return StyleSheet.create({
    root: {
      marginBottom: theme.spacing.lg,
    },
    banner: {
      backgroundColor: theme.background.inset,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.md,
      marginBottom: theme.spacing.md,
    },
    /** Text on dark inset — always high contrast. */
    bannerText: {
      ...theme.typography.caption,
      color: theme.white,
      textAlign: 'center',
      opacity: 0.92,
    },
    emptyStateCard: {
      backgroundColor: theme.background.card,
      borderRadius: theme.borderRadius.lg,
      paddingVertical: theme.spacing.xl,
      paddingHorizontal: theme.spacing.lg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.gray[300],
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
    },
    emptyStateTitle: {
      ...theme.typography.label,
      color: theme.gray[900],
      fontWeight: '700',
      textAlign: 'center',
    },
    emptyStateText: {
      ...theme.typography.bodySmall,
      color: theme.gray[700],
      textAlign: 'center',
      maxWidth: 380,
    },
    splitPitchWrap: {
      marginBottom: theme.spacing.md,
    },
    /** Formation strip: home kit+formation+coach | FORMATION | away (mirrored). */
    formStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.background.card,
      borderRadius: theme.borderRadius.lg,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      marginBottom: theme.spacing.md,
    },
    formBadge: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      minWidth: 0,
    },
    formBadgeAway: {
      flexDirection: 'row-reverse',
    },
    formBadgeText: {
      flexShrink: 1,
      minWidth: 0,
    },
    formBadgeTextAway: {
      alignItems: 'flex-end',
    },
    formNum: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.gray[900],
    },
    formMgr: {
      ...theme.typography.caption,
      color: theme.gray[500],
      marginTop: 1,
      maxWidth: 96,
    },
    formLabel: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1,
      color: theme.gray[500],
      paddingHorizontal: theme.spacing.sm,
    },
    pitchFrame: {
      width: '100%',
      aspectRatio: LINEUP_PITCH_FRAME_WIDTH_OVER_HEIGHT,
      borderRadius: theme.borderRadius.lg,
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: theme.background.inset,
      borderWidth: StyleSheet.hairlineWidth,
    },
    pitchFrameBorder: {
      borderColor: isDark ? theme.gray[200] : theme.gray[800],
    },
    pitchPlayersLayer: {
      ...StyleSheet.absoluteFillObject,
    },
    pitchPlayer: {
      position: 'absolute',
      alignItems: 'center',
    },
    pitchKitWrap: {
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
    },
    markerStrip: {
      position: 'absolute',
      top: -2,
      right: -2,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 2,
      maxWidth: 40,
      justifyContent: 'flex-end',
    },
    markerChip: {
      fontSize: 8,
      fontWeight: '800',
      overflow: 'hidden',
      minWidth: 14,
      textAlign: 'center',
    },
    markerGoal: {
      color: theme.success,
    },
    markerCard: {
      color: theme.warning,
    },
    markerSub: {
      color: theme.info,
    },
    /** On the green pitch — number left of name on one row; shadow keeps white text legible. */
    pitchPlayerLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 0,
      maxWidth: 104,
      gap: 4,
    },
    pitchPlayerNumInline: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.white,
      opacity: 0.9,
      flexShrink: 0,
      textShadowColor: 'rgba(0,0,0,0.55)',
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 3,
    },
    pitchPlayerNameInline: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.white,
      flexShrink: 1,
      textShadowColor: 'rgba(0,0,0,0.55)',
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 3,
    },
    /** Substitutes: small uppercase label + two compact team columns. */
    subsSection: {
      marginTop: theme.spacing.lg,
    },
    subsSectionTitle: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1,
      color: theme.gray[500],
      marginBottom: theme.spacing.sm,
    },
    subsRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: theme.spacing.md,
    },
    subsCard: {
      flex: 1,
      minWidth: 0,
      backgroundColor: theme.background.card,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.md,
    },
    subsHead: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.sm,
      marginBottom: theme.spacing.xs,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.gray[300],
    },
    subsAbbr: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.gray[800],
    },
    subRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingVertical: 5,
    },
    subNum: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.gray[500],
      width: 18,
      textAlign: 'center',
    },
    subName: {
      flex: 1,
      minWidth: 0,
      fontSize: 12.5,
      fontWeight: '500',
    },
    subNameOn: {
      color: theme.gray[900],
    },
    subNameOff: {
      color: theme.gray[600],
    },
  });
}

export type MatchHubLineupsStyles = ReturnType<typeof getMatchHubLineupsStyles>;
